import { redirect } from "@tanstack/react-router";
import { SESSION_STORE } from "src/api/session";
import { ERROR_STORE } from "src/context/error-context";
import { isFormError } from "src/utils/error";
import { DIAGNOSTICS } from "src/utils/diagnostics";
import {
    AddDeckCardRequest,
    AddScannerSessionEntryRequest,
    AddWatchListEntryRequest,
    AdminBugReportFilter,
    AdminListAccountsRequest,
    AdminListLogRequest,
    ArrangeRoundRequest,
    CollaboratorLevel,
    Configuration,
    DeletionMode,
    CreateCollectionRequest,
    CreateDeckRequest,
    CreateScannerSessionRequest,
    CreateDeckTagRequest,
    CreateGlobalTagRequest,
    CreateWatchListRequest,
    UpdateGlobalTagRequest,
    UpdateScannerSessionEntryRequest,
    UpdateScannerSessionRequest,
    DefaultApi,
    ImportDeckCardsRequest,
    ListCollectionCardsRequest,
    ListPublicCollectionCardsRequest,
    ListPublicTournamentsRequest,
    ListSharedCollectionCardsRequest,
    MailLanguage,
    MeResponse,
    NewCollectionEntry,
    PrintingLookupRequest,
    ReportResultRequest,
    RequiredError,
    ResponseError,
    Role,
    SearchPublicDecksRequest,
    SendBugReportRequest,
    SetAdvisorSettingsRequest,
    SetDeckRuleZeroRequest,
    SetResultRequest,
    SetRoundTimerRequest,
    SignupRequest,
    SplitCollectionEntryRequest,
    SubscribePushDeviceRequest,
    UpdateCollectionEntryRequest,
    UpdateCollectionRequest,
    SwapDeckCardRequest,
    TournamentSettingsRequest,
    ClaimMatchLifeRequest,
    CaptureMatchLifeConflictRequest,
    SaveMatchLifeRequest,
    UpdateDeckCardRequest,
    UpdateDeckRequest,
    UpdateDeckTagRequest,
    UpdateWatchListEntryRequest,
    UpdateWatchListRequest,
    Visibility,
} from "src/api/generated";

/** Hyphen separated uuid */
export type UUID = string;

const configuration = new Configuration({
    basePath: window.location.origin,
    middleware: [DIAGNOSTICS.middleware],
});
const defaultApi = new DefaultApi(configuration);

export const Api = {
    signup: {
        begin: async (req: SignupRequest) => handleError(defaultApi.signup({ SignupRequest: req })),
    },
    // The auth ceremonies deliberately bypass `handleError`. Their callers show a message per
    // failure — an unknown username reads differently from a cancelled prompt — and
    // `handleError` would report to `ERROR_STORE` first, which replaces the entire page with
    // the router's error screen before the form ever sees the rejection.
    auth: {
        // The username picks the account; its passkeys come back as the allow-list, so the
        // authenticator knows which credential to look for.
        startLogin: (username: string) => defaultApi.startLogin({ StartLoginRequest: { username } }),
        // Answers with no body — who was logged in is read back from `account.me`, so there
        // is exactly one place the session is derived from.
        finishLogin: (credential: unknown, rememberMe: boolean) =>
            defaultApi.finishLogin({ FinishLoginRequest: { credential, remember_me: rememberMe } }),
        // The "lost passkey" flow: mails a fresh registration link to the account's stored
        // address. Answers 200 whether or not the username exists. Bypasses `handleError`
        // like the ceremonies above — a rate-limited request should read as a note next to
        // the button, not replace the page with the error screen.
        recover: (username: string, language: MailLanguage) =>
            defaultApi.recoverAccount({ RecoverAccountRequest: { username, language } }),
        logout: async () => handleError(defaultApi.logout()),
        oidc: {
            providers: async () => handleError(defaultApi.listOpenidProviders()),
            start: (provider: string, rememberMe: boolean) =>
                defaultApi.startOidcLogin({ StartOidcLoginRequest: { provider, remember_me: rememberMe } }),
            pending: async () => handleError(defaultApi.getPendingOidcLogin()),
            chooseUsername: (username: string) => defaultApi.chooseUsername({ ChooseUsernameRequest: { username } }),
        },
    },
    accounts: {
        // Answers 401 for anyone not logged in, which is the normal case for a visitor —
        // reporting that would put the error screen in front of every public page.
        //
        // Takes a signal because the whole app chrome waits on this one read: a
        // gateway that accepts the connection and then never answers leaves the
        // fetch pending forever, and with it the navbar without a login button.
        me: async (signal?: AbortSignal): Promise<MeResponse | null> => {
            const response = await defaultApi.me({ signal });
            if (!isFormError(response)) return response;
            if (response.error.locked) SESSION_STORE.locked();
            return null;
        },
        passkeys: {
            list: async () => handleError(defaultApi.listPasskeys()),
            // Adding a device from an existing session; the ceremony failures are the caller's
            // to render, same as on login.
            startAdd: () => defaultApi.startAddPasskey(),
            finishAdd: (credential: unknown, label?: string) =>
                defaultApi.finishAddPasskey({ FinishAddPasskeyRequest: { credential, label } }),
            rename: async (uuid: string, label: string) =>
                handleError(defaultApi.renamePasskey({ uuid, RenamePasskeyRequest: { label } })),
            delete: async (uuid: string) => handleError(defaultApi.deletePasskey({ uuid })),
        },
        push: {
            contextual: {
                settings: () => localAccountAction(defaultApi.getPushSettings()),
                setTournamentPushes: (tournament_pushes: boolean) =>
                    localAccountAction(defaultApi.setPushSettings({ SetPushSettingsRequest: { tournament_pushes } })),
                subscribe: (request: SubscribePushDeviceRequest) =>
                    localAccountAction(defaultApi.subscribePushDevice({ SubscribePushDeviceRequest: request })),
                check: (endpoint: string) =>
                    localAccountAction(defaultApi.checkPushDevice({ PushDeviceRequest: { endpoint } })),
            },
            settings: async () => handleError(defaultApi.getPushSettings()),
            setTournamentPushes: async (tournament_pushes: boolean) =>
                handleError(defaultApi.setPushSettings({ SetPushSettingsRequest: { tournament_pushes } })),
            subscribe: async (request: SubscribePushDeviceRequest) =>
                handleError(defaultApi.subscribePushDevice({ SubscribePushDeviceRequest: request })),
            unsubscribe: async (endpoint: string) =>
                handleError(defaultApi.unsubscribePushDevice({ PushDeviceRequest: { endpoint } })),
            check: async (endpoint: string) =>
                handleError(defaultApi.checkPushDevice({ PushDeviceRequest: { endpoint } })),
        },
        rename: async (username: string) =>
            handleError(defaultApi.renameAccount({ RenameAccountRequest: { username } })),
        // Takes the account and everything private to it with it. Bypasses
        // `handleError`, since the typed username not matching is a message
        // next to the field rather than the error screen.
        delete: (username: string, appearAnonymously: boolean) =>
            defaultApi.deleteAccount({
                DeleteAccountRequest: { username, appear_anonymously: appearAnonymously },
            }),
    },
    // Answers 403 to anyone but an Admin. The admin pages check the Role before
    // asking, so reaching the error screen through here means the Role changed
    // underneath an open page.
    admin: {
        accounts: {
            list: async (query: AdminListAccountsRequest = {}) => handleError(defaultApi.adminListAccounts(query)),
            get: async (uuid: UUID) => handleError(defaultApi.adminGetAccount({ uuid })),
            setRole: async (uuid: UUID, role: Role) =>
                handleError(defaultApi.adminSetRole({ uuid, AdminSetRoleRequest: { role } })),
            lock: async (uuid: UUID, reason: string) =>
                handleError(defaultApi.adminLockAccount({ uuid, AdminLockAccountRequest: { reason } })),
            unlock: async (uuid: UUID, reason?: string) =>
                handleError(defaultApi.adminUnlockAccount({ uuid, AdminUnlockAccountRequest: { reason } })),
            rename: async (uuid: UUID, username: string, reason: string, language: MailLanguage) =>
                handleError(
                    defaultApi.adminRenameAccount({ uuid, AdminRenameAccountRequest: { username, reason, language } }),
                ),
            delete: async (uuid: UUID, username: string, mode: DeletionMode) =>
                handleError(defaultApi.adminDeleteAccount({ uuid, AdminDeleteAccountRequest: { username, mode } })),
        },
        log: {
            list: async (query: AdminListLogRequest = {}) => handleError(defaultApi.adminListLog(query)),
        },
        bugReports: {
            list: async (filter: AdminBugReportFilter, after?: number) =>
                handleError(defaultApi.adminListBugReports({ filter, after })),
            get: async (number: number) => handleError(defaultApi.adminGetBugReport({ number })),
            imageUrl: (number: number, image: UUID) => `/api/frontend/v1/admin/bug-reports/${number}/images/${image}`,
            markDone: async (number: number) => handleError(defaultApi.adminMarkBugReportDone({ number })),
            reopen: async (number: number) => handleError(defaultApi.adminReopenBugReport({ number })),
            setNote: async (number: number, note: string) =>
                handleError(defaultApi.adminSetBugReportNote({ number, AdminSetBugReportNoteRequest: { note } })),
        },
        statistics: async () => handleError(defaultApi.adminGetStatistics()),
        syncs: {
            get: async () => handleError(defaultApi.adminGetSyncs()),
        },
    },
    bugReports: {
        send: (report: SendBugReportRequest, images: Blob[]) => defaultApi.sendBugReport({ report, images }),
    },
    collections: {
        list: async () => handleError(defaultApi.getAllCollections()),
        get: async (uuid: UUID) => handleError(defaultApi.getCollection({ collection: uuid })),
        getIfThere: async (uuid: UUID) => orGone(defaultApi.getCollection({ collection: uuid })),
        create: async (req: CreateCollectionRequest) =>
            handleError(defaultApi.createCollection({ CreateCollectionRequest: req })),
        update: async (uuid: UUID, req: UpdateCollectionRequest) =>
            handleError(defaultApi.updateCollection({ collection: uuid, UpdateCollectionRequest: req })),
        // Cascades to every entry in the collection.
        delete: async (uuid: UUID) => handleError(defaultApi.deleteCollection({ collection: uuid })),
        // One page of a collection, sorted and filtered by the database and
        // carrying the card data with it. This is what the card list reads;
        // `entries` below still exists for the few places that genuinely need
        // every row at once.
        cards: async (collection: UUID, query: Omit<ListCollectionCardsRequest, "collection"> = {}) =>
            handleError(defaultApi.listCollectionCards({ collection, ...query })),
        cardsIfThere: async (collection: UUID, query: Omit<ListCollectionCardsRequest, "collection"> = {}) =>
            orGone(defaultApi.listCollectionCards({ collection, ...query })),
        // Everything the statistics tab draws, counted server-side — one
        // request instead of every entry plus a Scryfall lookup per printing.
        // What this box has lent to decks. Those cards are not rows of the box
        // any more, so the page asks for them separately.
        onLoan: async (collection: UUID) => handleError(defaultApi.listCollectionOnLoan({ collection })),
        onLoanIfThere: async (collection: UUID) => orGone(defaultApi.listCollectionOnLoan({ collection })),
        statistics: async (collection: UUID) => handleError(defaultApi.getCollectionStatistics({ collection })),
        statisticsIfThere: async (collection: UUID) => orGone(defaultApi.getCollectionStatistics({ collection })),
        entries: {
            list: async (collection: UUID) => handleError(defaultApi.listCollectionEntries({ collection })),
            add: async (collection: UUID, entries: Array<NewCollectionEntry>) =>
                handleError(
                    defaultApi.addCollectionEntries({
                        collection,
                        AddCollectionEntriesRequest: { entries },
                    }),
                ),
            // Partial: a field left out of `req` is left alone server-side, and
            // `null` on one of the nullable ones clears it. `JSON.stringify`
            // drops `undefined`, which is what makes the distinction survive
            // the wire.
            update: async (collection: UUID, entry: UUID, req: UpdateCollectionEntryRequest) =>
                handleError(
                    defaultApi.updateCollectionEntry({
                        collection,
                        entry,
                        UpdateCollectionEntryRequest: req,
                    }),
                ),
            // Moves copies out of a stack into a new one; answers with both.
            split: async (collection: UUID, entry: UUID, req: SplitCollectionEntryRequest) =>
                handleError(
                    defaultApi.splitCollectionEntry({
                        collection,
                        entry,
                        SplitCollectionEntryRequest: req,
                    }),
                ),
            // Folds stacks of the same printing, condition and finish into the
            // oldest of them, which is the one that comes back.
            merge: async (collection: UUID, entries: Array<UUID>) =>
                handleError(
                    defaultApi.mergeCollectionEntries({
                        collection,
                        MergeCollectionEntriesRequest: { entries },
                    }),
                ),
            delete: async (collection: UUID, entry: UUID) =>
                handleError(defaultApi.deleteCollectionEntry({ collection, entry })),
            tag: async (collection: UUID, entry: UUID, tag: UUID) =>
                handleError(defaultApi.assignCollectionEntryTag({ collection, entry, tag })),
            untag: async (collection: UUID, entry: UUID, tag: UUID) =>
                handleError(defaultApi.unassignCollectionEntryTag({ collection, entry, tag })),
        },
        // Visibility is its own endpoint, not part of `update` — switching to
        // `Unlisted` mints a share token and switching away revokes it, which is
        // not something a rename should do as a side effect.
        setVisibility: async (uuid: UUID, visibility: Visibility) =>
            handleError(
                defaultApi.setVisibilityCollection({
                    collection: uuid,
                    SetCollectionVisibilityRequest: { visibility },
                }),
            ),
        rotateShareToken: async (uuid: UUID) => handleError(defaultApi.rotateShareToken({ collection: uuid })),
    },
    decks: {
        list: async () => handleError(defaultApi.getAllDecks()),
        sharedWithMe: async () => handleError(defaultApi.getDecksSharedWithMe()),
        leave: async (uuid: UUID) => handleError(defaultApi.leaveDeck({ deck: uuid })),
        get: async (uuid: UUID) => handleError(defaultApi.getDeck({ deck: uuid })),
        getIfThere: async (uuid: UUID) => orGone(defaultApi.getDeck({ deck: uuid })),
        create: async (req: CreateDeckRequest) => handleError(defaultApi.createDeck({ CreateDeckRequest: req })),
        update: async (uuid: UUID, req: UpdateDeckRequest) =>
            handleError(defaultApi.updateDeck({ deck: uuid, UpdateDeckRequest: req })),
        delete: async (uuid: UUID) => handleError(defaultApi.deleteDeck({ deck: uuid })),
        history: async (deck: UUID, after: UUID | null = null) =>
            handleError(defaultApi.getDeckHistory({ deck, after })),
        undo: async (deck: UUID) => handleError(defaultApi.undoDeckChange({ deck })),
        redo: async (deck: UUID) => handleError(defaultApi.redoDeckChange({ deck })),
        point: async (deck: UUID, change: UUID) => handleError(defaultApi.getDeckPoint({ deck, change })),
        reset: async (deck: UUID, change: UUID) => handleError(defaultApi.resetDeck({ deck, change })),
        setCheckpoint: async (deck: UUID, change: UUID, name: string) =>
            handleError(defaultApi.setDeckCheckpoint({ deck, change, SetDeckCheckpointRequest: { name } })),
        removeCheckpoint: async (deck: UUID, change: UUID) =>
            handleError(defaultApi.removeDeckCheckpoint({ deck, change })),
        setVisibility: async (uuid: UUID, visibility: Visibility) =>
            handleError(defaultApi.setVisibilityDeck({ deck: uuid, SetDeckVisibilityRequest: { visibility } })),
        rotateShareToken: async (uuid: UUID) => handleError(defaultApi.rotateDeckShareToken({ deck: uuid })),
        collaborators: {
            list: async (deck: UUID) => handleError(defaultApi.listDeckCollaborators({ deck })),
            candidates: async (deck: UUID, query: string) =>
                quietly(defaultApi.searchCollaboratorCandidates({ deck, query })),
            put: async (deck: UUID, username: string, level: CollaboratorLevel) =>
                handleError(defaultApi.putDeckCollaborator({ deck, PutDeckCollaboratorRequest: { username, level } })),
            remove: async (deck: UUID, account: UUID) =>
                handleError(defaultApi.removeDeckCollaborator({ deck, account })),
        },
        // `null` hands the decision back to the commander zone. Its own endpoint
        // because it is set from the legality band, not from the deck's form.
        setColors: async (uuid: UUID, colors: string | null) =>
            handleError(defaultApi.setDeckColors({ deck: uuid, SetDeckColorsRequest: { colors } })),
        // Which Commander bracket the deck claims to be, `null` for unsaid.
        setBracket: async (uuid: UUID, bracket: number | null) =>
            handleError(defaultApi.setDeckBracket({ deck: uuid, SetDeckBracketRequest: { bracket } })),
        // What the table agreed to play this deck under, colours included. All
        // at once: they are one conversation, the dialog edits them as one
        // form, and the Deck history records them as one Deck change.
        setRuleZero: async (uuid: UUID, rules: SetDeckRuleZeroRequest) =>
            handleError(defaultApi.setDeckRuleZero({ deck: uuid, SetDeckRuleZeroRequest: rules })),
        // Everything the advisor knows about this deck for this reader: which
        // themes to argue for, the shape to grade against, what a card may
        // cost, and the two lists of cards it has been told to leave alone. One
        // document, replaced whole — every writer already rewrites its own slice.
        advisorSettings: {
            get: async (uuid: UUID) => handleError(defaultApi.getDeckAdvisorSettings({ deck: uuid })),
            save: async (uuid: UUID, settings: SetAdvisorSettingsRequest) =>
                handleError(defaultApi.setDeckAdvisorSettings({ deck: uuid, SetAdvisorSettingsRequest: settings })),
        },
        // What every format asks of a deck built for it. Constant per release,
        // so the deck pages read it once through their loader.
        formats: async () => handleError(defaultApi.getDeckFormats()),
        // The service reads the deck behind a link to another builder — the
        // browser cannot, those sites answer no cross-origin request.
        readUrl: async (url: string) => handleError(defaultApi.readDeckUrl({ ReadDeckUrlRequest: { url } })),
        // Which shelf the deck stands on. `null` takes it off every one of
        // them; the archive is a folder like any other, see `folders` below.
        // A filed deck keeps its cards either way.
        setFolder: async (uuid: UUID, folder: UUID | null) =>
            handleError(defaultApi.setDeckFolder({ deck: uuid, SetDeckFolderRequest: { folder } })),
        collection: {
            // Start keeping the cards that are physically in the deck. Idempotent.
            attach: async (deck: UUID) => handleError(defaultApi.attachDeckCollection({ deck })),
            // Only while nothing is filed in it.
            detach: async (deck: UUID) => handleError(defaultApi.detachDeckCollection({ deck })),
        },
        // Where the deck list and the cardboard filed under it disagree. Its
        // own request because the deck's header asks it on every tab, and the
        // sourcing answer would drag the whole account's shelf along.
        drift: async (deck: UUID) => handleError(defaultApi.getDeckCollectionDrift({ deck })),
        driftIfOwner: async (deck: UUID) => orGone(defaultApi.getDeckCollectionDrift({ deck })),
        sourcing: {
            // What the deck asks for, what is in it, and where the rest could
            // come from — three flat lists the client matches up itself.
            read: async (deck: UUID) => handleError(defaultApi.getDeckSourcing({ deck })),
            // Move copies out of a box and into the deck, remembering the box.
            take: async (deck: UUID, entry: UUID, quantity: number, slot: UUID | null = null) =>
                handleError(defaultApi.takeDeckCards({ deck, TakeDeckCardsRequest: { entry, quantity, slot } })),
            // The way back. `target` is only needed for cards that remember no
            // origin, which is what was bought straight into the deck.
            returnCards: async (deck: UUID, entry: UUID, quantity: number, target: UUID | null = null) =>
                handleError(defaultApi.returnDeckCards({ deck, ReturnDeckCardsRequest: { entry, quantity, target } })),
            // Says the deck already holds what its list asks for, which is how a
            // deck imported from elsewhere gets its cards without a shopping trip.
            fill: async (deck: UUID, slot: UUID | null = null) =>
                handleError(defaultApi.fillDeckCollection({ deck, FillDeckCollectionRequest: { slot } })),
            // Taking the whole deck apart.
            returnAll: async (deck: UUID, target: UUID | null = null) =>
                handleError(defaultApi.returnAllDeckCards({ deck, ReturnAllDeckCardsRequest: { target } })),
        },
        cards: {
            // The whole deck in one answer, catalog data and tags included.
            list: async (deck: UUID) => handleError(defaultApi.listDeckCards({ deck })),
            listIfThere: async (deck: UUID) => orGone(defaultApi.listDeckCards({ deck })),
            add: async (deck: UUID, req: AddDeckCardRequest) =>
                handleError(defaultApi.addDeckCard({ deck, AddDeckCardRequest: req })),
            update: async (deck: UUID, card: UUID, req: UpdateDeckCardRequest) =>
                handleError(defaultApi.updateDeckCard({ deck, card, UpdateDeckCardRequest: req })),
            delete: async (deck: UUID, card: UUID) => handleError(defaultApi.deleteDeckCard({ deck, card })),
            /**
             * Swaps copies of one Deck entry for another card in its zone, whole or not at all
             *
             * @param deck the deck
             * @param card the Deck entry giving up copies
             * @param req how many copies and the card coming in
             *
             * @returns the Deck entry the incoming card sits in
             */
            swap: async (deck: UUID, card: UUID, req: SwapDeckCardRequest) =>
                handleError(defaultApi.swapDeckCard({ deck, card, SwapDeckCardRequest: req })),
            // A pasted decklist in one transaction: it either lands whole or
            // not at all.
            import: async (deck: UUID, req: ImportDeckCardsRequest) =>
                handleError(defaultApi.importDeckCards({ deck, ImportDeckCardsRequest: req })),
            tag: async (deck: UUID, card: UUID, tag: UUID) =>
                handleError(defaultApi.assignDeckCardTag({ deck, card, tag })),
            untag: async (deck: UUID, card: UUID, tag: UUID) =>
                handleError(defaultApi.unassignDeckCardTag({ deck, card, tag })),
        },
        tags: {
            create: async (deck: UUID, req: CreateDeckTagRequest) =>
                handleError(defaultApi.createDeckTag({ deck, CreateDeckTagRequest: req })),
            update: async (deck: UUID, tag: UUID, req: UpdateDeckTagRequest) =>
                handleError(defaultApi.updateDeckTag({ deck, tag, UpdateDeckTagRequest: req })),
            delete: async (deck: UUID, tag: UUID) => handleError(defaultApi.deleteDeckTag({ deck, tag })),
        },
    },
    // Bypasses `handleError` like the auth ceremonies above: a revoked, replaced
    // or mistyped link is the normal way for these to fail, and the pages
    // behind a share link say so themselves.
    tags: {
        list: async () => handleError(defaultApi.getAllGlobalTags()),
        create: async (req: CreateGlobalTagRequest) =>
            handleError(defaultApi.createGlobalTag({ CreateGlobalTagRequest: req })),
        update: async (tag: UUID, req: UpdateGlobalTagRequest) =>
            handleError(defaultApi.updateGlobalTag({ tag, UpdateGlobalTagRequest: req })),
        delete: async (tag: UUID) => handleError(defaultApi.deleteGlobalTag({ tag })),
    },
    // What accounts put on show: searchable, readable by anyone, and reached
    // without a token. Bypasses `handleError` like the share links above — a
    // deck that has just been made private again is a normal way for these to
    // fail, and the pages say so themselves rather than replacing the app with
    // the error screen.
    explore: {
        decks: {
            search: (query: SearchPublicDecksRequest = {}) => defaultApi.searchPublicDecks(query),
            get: (deck: UUID) => defaultApi.getPublicDeck({ deck }),
            cards: (deck: UUID) => defaultApi.listPublicDeckCards({ deck }),
        },
        profiles: {
            get: (username: string) => defaultApi.getPublicProfile({ username }),
        },
        collections: {
            get: (collection: UUID) => defaultApi.getPublicCollection({ collection }),
            cards: (collection: UUID, query: Omit<ListPublicCollectionCardsRequest, "collection"> = {}) =>
                defaultApi.listPublicCollectionCards({ collection, ...query }),
            statistics: (collection: UUID) => defaultApi.getPublicCollectionStatistics({ collection }),
        },
    },
    shared: {
        decks: {
            get: (token: string) => defaultApi.getSharedDeck({ token }),
            cards: (token: string) => defaultApi.listSharedDeckCards({ token }),
        },
        tournaments: {
            get: (token: string) => defaultApi.getSharedTournament({ token }),
            participants: (token: string) => defaultApi.listSharedTournamentParticipants({ token }),
            // The way into an unlisted Tournament: its id alone does not let an Account in.
            signUp: async (token: string) => handleError(defaultApi.signUpForSharedTournament({ token })),
            // Never a previewed Round: the link shows what anybody sees.
            rounds: (token: string) => defaultApi.listSharedTournamentRounds({ token }),
            standings: (token: string) => defaultApi.getSharedTournamentStandings({ token }),
            // The shared lists once the Tournament is Finished, none before.
            lists: (token: string) => defaultApi.listSharedTournamentRegisteredLists({ token }),
        },
        collections: {
            get: (token: string) => defaultApi.getSharedCollection({ token }),
            cards: (token: string, query: Omit<ListSharedCollectionCardsRequest, "token"> = {}) =>
                defaultApi.listSharedCollectionCards({ token, ...query }),
            statistics: (token: string) => defaultApi.getSharedCollectionStatistics({ token }),
        },
    },
    // The shelves decks are filed on. Reachable without naming a deck: they
    // exist before anything is filed into one, and the archive is handed out
    // whether or not the account has ever put a deck away.
    folders: {
        list: async () => handleError(defaultApi.getAllDeckFolders()),
        create: async (name: string) => handleError(defaultApi.createDeckFolder({ CreateDeckFolderRequest: { name } })),
        rename: async (uuid: UUID, name: string) =>
            handleError(defaultApi.updateDeckFolder({ folder: uuid, UpdateDeckFolderRequest: { name } })),
        // The decks in it are not touched; they turn up among the unfiled ones.
        delete: async (uuid: UUID) => handleError(defaultApi.deleteDeckFolder({ folder: uuid })),
    },
    // The scanner's staging areas, kept server-side so a box sorted on a phone
    // can be corrected and filed from a desk. Everything here is per account.
    scannerSessions: {
        list: async () => handleError(defaultApi.getAllScannerSessions()),
        get: async (session: UUID) => handleError(defaultApi.getScannerSession({ session })),
        create: async (req: CreateScannerSessionRequest) =>
            handleError(defaultApi.createScannerSession({ CreateScannerSessionRequest: req })),
        update: async (session: UUID, req: UpdateScannerSessionRequest) =>
            handleError(defaultApi.updateScannerSession({ session, UpdateScannerSessionRequest: req })),
        // Takes the staging area with it, never the cards already filed.
        delete: async (session: UUID) => handleError(defaultApi.deleteScannerSession({ session })),
        // Files every staged stack into a collection and empties the session,
        // both inside one transaction. `null` uses the session's own choice.
        file: async (session: UUID, collection: UUID | null) =>
            handleError(defaultApi.fileScannerSession({ session, FileScannerSessionRequest: { collection } })),
        entries: {
            add: async (session: UUID, req: AddScannerSessionEntryRequest) =>
                handleError(defaultApi.addScannerSessionEntry({ session, AddScannerSessionEntryRequest: req })),
            // Partial, like a collection stack: a field left out stays as it is.
            update: async (session: UUID, entry: UUID, req: UpdateScannerSessionEntryRequest) =>
                handleError(
                    defaultApi.updateScannerSessionEntry({
                        session,
                        entry,
                        UpdateScannerSessionEntryRequest: req,
                    }),
                ),
            delete: async (session: UUID, entry: UUID) =>
                handleError(defaultApi.deleteScannerSessionEntry({ session, entry })),
        },
        // The same calls without `handleError`, for the buffer that empties
        // itself in the background. A scanner is used where there is no signal
        // on purpose, and a failed background write must leave the cards in the
        // local buffer and try again later — not replace the viewfinder with
        // the error screen.
        quiet: {
            list: () => defaultApi.getAllScannerSessions(),
            get: (session: UUID) => defaultApi.getScannerSession({ session }),
            create: (req: CreateScannerSessionRequest) =>
                defaultApi.createScannerSession({ CreateScannerSessionRequest: req }),
            addEntry: (session: UUID, req: AddScannerSessionEntryRequest) =>
                defaultApi.addScannerSessionEntry({ session, AddScannerSessionEntryRequest: req }),
            updateEntry: (session: UUID, entry: UUID, req: UpdateScannerSessionEntryRequest) =>
                defaultApi.updateScannerSessionEntry({ session, entry, UpdateScannerSessionEntryRequest: req }),
        },
    },
    // The community's custom card art, searched through the service: MPCFill
    // answers cross-origin requests for their own site only, so the browser
    // cannot ask them directly. Quiet on failure — their index is somebody
    // else's server, and it being down must cost the picker its thumbnails,
    // not the page.
    mpcfill: {
        art: (names: Array<string>) => defaultApi.searchMpcfillArt({ MpcFillSearchRequest: { names } }),
        cardbacks: () => defaultApi.getMpcfillCardbacks(),
    },
    printings: {
        // The service's own copy of Scryfall's catalog, asked in bulk. This is
        // what an import places its rows against — see `printing-catalog.ts`,
        // which batches and de-duplicates on the way here.
        resolve: async (lookups: Array<PrintingLookupRequest>) =>
            handleError(defaultApi.resolvePrintings({ ResolvePrintingsRequest: { lookups } })),
        // The same lookup for callers that decorate rather than build: the
        // advisor resolves names only to show artwork and a price. Bypasses
        // `handleError` like the ceremonies above, because reporting would
        // replace the whole page with the error screen over a missing
        // thumbnail — the caller renders plain rows instead.
        resolveQuietly: (lookups: Array<PrintingLookupRequest>) =>
            defaultApi.resolvePrintings({ ResolvePrintingsRequest: { lookups } }),
        bracketFlags: (printings: Array<string>) => defaultApi.getBracketFlags({ BracketFlagsRequest: { printings } }),
        // What a card has cost, day by day, out of Cardmarket's price guide.
        // Quiet like `resolveQuietly`: the history decorates a card that is
        // already on screen, and a chart that cannot be drawn must not replace
        // the dialog it sits in with the error screen.
        priceHistoryQuietly: (printing: UUID) => defaultApi.getPriceHistory({ printing }),
        // Every language the same card exists in. A printing is one language,
        // so this is what changing a stack's language picks from — the catalog
        // already holds them all, nothing is fetched from Scryfall for it.
        languages: async (printing: UUID) => handleError(defaultApi.getPrintingLanguages({ printing })),
    },
    // Reads are open to anybody and answer 400 for what the reader may not see,
    // so the public ones skip `handleError` like `explore`. The writes answer
    // form errors for settings the Ruleset or Table size refuse.
    tournaments: {
        listPublic: (query: ListPublicTournamentsRequest = {}) => defaultApi.listPublicTournaments(query),
        get: (tournament: UUID) => defaultApi.getTournament({ tournament }),
        mine: async () => handleError(defaultApi.getMyTournaments()),
        mineForNavigation: () => quietSessionRead(defaultApi.getMyTournaments()),
        create: async (req: TournamentSettingsRequest) =>
            handleError(defaultApi.createTournament({ TournamentSettingsRequest: req })),
        update: async (tournament: UUID, req: TournamentSettingsRequest) =>
            handleError(defaultApi.updateTournament({ tournament, TournamentSettingsRequest: req })),
        delete: async (tournament: UUID) => handleError(defaultApi.deleteTournament({ tournament })),
        // Every Organizer reads and names Co-organizers; the Owner takes off
        // any, a Co-organizer only itself.
        organizers: {
            list: async (tournament: UUID) => handleError(defaultApi.listTournamentOrganizers({ tournament })),
            add: async (tournament: UUID, username: string) =>
                handleError(defaultApi.addTournamentCoOrganizer({ tournament, NameAccountRequest: { username } })),
            remove: async (tournament: UUID, account: UUID) =>
                handleError(defaultApi.removeTournamentCoOrganizer({ tournament, account })),
        },
        // The Owner offers the Tournament to one Account at a time; nothing
        // changes until that Account accepts.
        handover: {
            offer: async (tournament: UUID, username: string) =>
                handleError(defaultApi.offerTournamentHandover({ tournament, NameAccountRequest: { username } })),
            withdraw: async (tournament: UUID) => handleError(defaultApi.withdrawTournamentHandover({ tournament })),
            offers: async () => handleError(defaultApi.getTournamentHandovers()),
            accept: async (tournament: UUID) => handleError(defaultApi.acceptTournamentHandover({ tournament })),
            decline: async (tournament: UUID) => handleError(defaultApi.declineTournamentHandover({ tournament })),
        },
        // Organizers add and remove Participants while the Tournament is in
        // Registration, and add late ones until the Swiss Rounds are over;
        // Accounts sign up themselves where the Owner allows it. Once the
        // first Round started, Participants Drop instead of leaving.
        participants: {
            list: (tournament: UUID) => defaultApi.listTournamentParticipants({ tournament }),
            addAccount: async (tournament: UUID, username: string) =>
                handleError(
                    defaultApi.addTournamentAccount({ tournament, AddAccountParticipantRequest: { username } }),
                ),
            addGuest: async (tournament: UUID, name: string) =>
                handleError(defaultApi.addTournamentGuest({ tournament, AddGuestParticipantRequest: { name } })),
            remove: async (tournament: UUID, participant: UUID) =>
                handleError(defaultApi.removeTournamentParticipant({ tournament, participant })),
            signUp: async (tournament: UUID) => handleError(defaultApi.signUpForTournament({ tournament })),
            withdraw: async (tournament: UUID) => handleError(defaultApi.withdrawTournamentSignUp({ tournament })),
            drop: async (tournament: UUID, participant: UUID) =>
                handleError(defaultApi.dropTournamentParticipant({ tournament, participant })),
            dropOut: async (tournament: UUID) => handleError(defaultApi.dropOutOfTournament({ tournament })),
            // Only with an entry fee: the unpaid are left out of the pairing.
            setPaid: async (tournament: UUID, participant: UUID, paid: boolean) =>
                handleError(
                    defaultApi.setTournamentParticipantPaid({ tournament, participant, SetPaidRequest: { paid } }),
                ),
            setCheckedIn: async (tournament: UUID, participant: UUID, checkedIn: boolean) =>
                handleError(
                    defaultApi.setTournamentParticipantCheckedIn({
                        tournament,
                        participant,
                        SetCheckedInRequest: { checked_in: checkedIn },
                    }),
                ),
            checkInEveryone: async (tournament: UUID) =>
                handleError(defaultApi.checkInTournamentParticipants({ tournament })),
        },
        decks: {
            mine: async (tournament: UUID) => handleError(defaultApi.getMyTournamentDeck({ tournament })),
            register: async (tournament: UUID, deck: UUID, shared: boolean) =>
                handleError(defaultApi.registerMyTournamentDeck({ tournament, RegisterDeckRequest: { deck, shared } })),
            setSharing: async (tournament: UUID, shared: boolean) =>
                handleError(defaultApi.setMyTournamentListSharing({ tournament, SetListSharingRequest: { shared } })),
            // Every list for Organizers; for anybody else the shared ones once
            // the Tournament is Finished. Open like `get`.
            lists: (tournament: UUID) => defaultApi.listTournamentRegisteredLists({ tournament }),
            remove: async (tournament: UUID) => handleError(defaultApi.removeMyTournamentDeck({ tournament })),
            list: async (tournament: UUID) => handleError(defaultApi.listTournamentRegisteredDecks({ tournament })),
            candidates: async (tournament: UUID, participant: UUID) =>
                handleError(defaultApi.listTournamentParticipantDecks({ tournament, participant })),
            registerFor: async (tournament: UUID, participant: UUID, deck: UUID) =>
                handleError(
                    defaultApi.registerTournamentParticipantDeck({
                        tournament,
                        participant,
                        RegisterDeckRequest: { deck },
                    }),
                ),
            removeFor: async (tournament: UUID, participant: UUID) =>
                handleError(defaultApi.removeTournamentParticipantDeck({ tournament, participant })),
        },
        // Running it: Organizers pair a Round as a preview only they see, start
        // it, set the Results and end it. The reads are open like `get`.
        matchRecovery: {
            tournament: (tournament: UUID) => defaultApi.getTournament({ tournament }),
            play: (tournament: UUID, match: UUID) => defaultApi.getTournamentMatchPlay({ tournament, match }),
        },
        lifeHistory: {
            release: (tournament: UUID, match: UUID, request: ClaimMatchLifeRequest) =>
                defaultApi.releaseMatchLifeHistory({ tournament, match, ClaimMatchLifeRequest: request }),
            takeover: (tournament: UUID, match: UUID, request: ClaimMatchLifeRequest) =>
                defaultApi.takeoverMatchLifeHistory({ tournament, match, ClaimMatchLifeRequest: request }),
            conflicts: (tournament: UUID, match: UUID) => defaultApi.listMatchLifeConflicts({ tournament, match }),
            conflict: (tournament: UUID, match: UUID, conflict: UUID) =>
                defaultApi.getMatchLifeConflict({ tournament, match, conflict }),
            captureConflict: (tournament: UUID, match: UUID, request: CaptureMatchLifeConflictRequest) =>
                defaultApi.captureMatchLifeConflict({ tournament, match, CaptureMatchLifeConflictRequest: request }),
            get: (tournament: UUID, match: UUID) => defaultApi.getMatchLifeHistory({ tournament, match }),
            claim: (tournament: UUID, match: UUID, request: ClaimMatchLifeRequest) =>
                defaultApi.claimMatchLifeHistory({ tournament, match, ClaimMatchLifeRequest: request }),
            save: (tournament: UUID, match: UUID, request: SaveMatchLifeRequest) =>
                defaultApi.saveMatchLifeHistory({ tournament, match, SaveMatchLifeRequest: request }),
        },
        rounds: {
            list: (tournament: UUID) => defaultApi.listTournamentRounds({ tournament }),
            standings: (tournament: UUID) => defaultApi.getTournamentStandings({ tournament }),
            // The reader's own Match in the running Round with the round
            // timer; open to any Account that sees the Tournament.
            play: async (tournament: UUID, match: UUID) =>
                handleError(defaultApi.getTournamentMatchPlay({ tournament, match })),
            myMatch: async (tournament: UUID) => handleError(defaultApi.getMyTournamentMatch({ tournament })),
            pair: async (tournament: UUID) => handleError(defaultApi.pairTournamentRound({ tournament })),
            discard: async (tournament: UUID, number: number) =>
                handleError(defaultApi.discardTournamentRound({ tournament, number })),
            start: async (tournament: UUID, number: number) =>
                handleError(defaultApi.startTournamentRound({ tournament, number })),
            end: async (tournament: UUID, number: number) =>
                handleError(defaultApi.endTournamentRound({ tournament, number })),
            // The whole previewed Round at once; starting it starts this layout.
            arrange: async (tournament: UUID, number: number, req: ArrangeRoundRequest) =>
                handleError(defaultApi.arrangeTournamentRound({ tournament, number, ArrangeRoundRequest: req })),
            // The latest ended Round runs again, with its Results.
            takeBack: async (tournament: UUID, number: number) =>
                handleError(defaultApi.takeBackTournamentRound({ tournament, number })),
            setTimer: async (tournament: UUID, number: number, req: SetRoundTimerRequest) =>
                handleError(defaultApi.setTournamentRoundTimer({ tournament, number, SetRoundTimerRequest: req })),
            // Also corrects a Result, at any time once the Round started.
            setResult: async (tournament: UUID, match: UUID, req: SetResultRequest) =>
                handleError(defaultApi.setTournamentMatchResult({ tournament, match, SetResultRequest: req })),
            setSwissRounds: async (tournament: UUID, swissRounds: number) =>
                handleError(
                    defaultApi.setTournamentSwissRounds({
                        tournament,
                        SetSwissRoundsRequest: { swiss_rounds: swissRounds },
                    }),
                ),
        },
        // A Participant reports its own Match's Result, which counts at once,
        // or disputes one reported by another Participant of the Match while
        // the Round runs. Organizers settle through `rounds.setResult`.
        results: {
            report: async (tournament: UUID, match: UUID, req: ReportResultRequest) =>
                handleError(defaultApi.reportTournamentMatchResult({ tournament, match, ReportResultRequest: req })),
            dispute: async (tournament: UUID, match: UUID) =>
                handleError(defaultApi.disputeTournamentMatchResult({ tournament, match })),
        },
        // Keeps the Rounds played so far; only a previewed one goes.
        cancel: async (tournament: UUID) => handleError(defaultApi.cancelTournament({ tournament })),
    },
    // The cards an account is still after. Each entry carries its own two
    // switches, so how strictly a copy or a price counts is answered
    // server-side and the client is handed numbers rather than the whole shelf.
    watchLists: {
        list: async () => handleError(defaultApi.getAllWatchLists()),
        create: async (req: CreateWatchListRequest) =>
            handleError(defaultApi.createWatchList({ CreateWatchListRequest: req })),
        update: async (uuid: UUID, req: UpdateWatchListRequest) =>
            handleError(defaultApi.updateWatchList({ list: uuid, UpdateWatchListRequest: req })),
        // Cascades to every entry on the list. Collections are untouched.
        delete: async (uuid: UUID) => handleError(defaultApi.deleteWatchList({ list: uuid })),
        // Everything the page draws: catalog data, stock counts and alarm state
        // in one request.
        entries: async (uuid: UUID) => handleError(defaultApi.listWatchListEntries({ list: uuid })),
        entriesIfThere: async (uuid: UUID) => orGone(defaultApi.listWatchListEntries({ list: uuid })),
        // Reachable without naming a list, because the navigation badge is
        // drawn from it wherever the reader happens to be.
        alarms: async () => handleError(defaultApi.getWatchListAlarms()),
        alarmsForNavigation: () => quietSessionRead(defaultApi.getWatchListAlarms()),
        entry: {
            add: async (list: UUID, req: AddWatchListEntryRequest) =>
                handleError(defaultApi.addWatchListEntry({ list, AddWatchListEntryRequest: req })),
            // Partial, like the collection entries: a field left out of `req` is
            // left alone server-side, and `null` on the alarm price clears it.
            // Anything the alarm reads disarms it when it changes.
            update: async (list: UUID, entry: UUID, req: UpdateWatchListEntryRequest) =>
                handleError(defaultApi.updateWatchListEntry({ list, entry, UpdateWatchListEntryRequest: req })),
            delete: async (list: UUID, entry: UUID) => handleError(defaultApi.deleteWatchListEntry({ list, entry })),
            // Which stacks the entry counts and where they lie. Asked when a
            // row is opened, not with the list: most rows are never opened.
            copies: async (list: UUID, entry: UUID) => handleError(defaultApi.listWatchListCopies({ list, entry })),
            // Records that the alarm was read. The alarm itself stays until the
            // price rises back through the threshold, because it is still true.
            acknowledge: async (list: UUID, entry: UUID) =>
                handleError(defaultApi.acknowledgeWatchListAlarm({ list, entry })),
        },
    },
    register: {
        // Called twice per registration: once on mount to validate the token and read the
        // username, once on submit for the ceremony the browser actually answers. Both
        // failures are shown on the page itself, hence no `handleError` here either.
        start: (token: string) => defaultApi.startRegistration({ StartRegistrationRequest: { token } }),
        finish: (token: string, credential: unknown) =>
            defaultApi.finishRegistration({ FinishRegistrationRequest: { token, credential } }),
    },
};

/**
 * Answers `null` where the server refused because the thing is gone
 *
 * A request the api refuses is reported and lands on the error screen, which is
 * right for a broken call and wrong for a stale link: a collection deleted in
 * another tab is still on the shelf of a page opened before that, and hovering
 * it must not take the app down. The refusal is deliberately the same answer
 * for "not there" and "not yours", so both come back as nothing to show.
 *
 * @param promise the raw call, unreported
 *
 * @returns what came back, or `null` where the request was refused
 */
async function orGone<T>(promise: Promise<T>): Promise<T | null> {
    try {
        return await promise;
    } catch (error) {
        if (error instanceof ResponseError && error.response.status === 400) return null;
        return handleError(Promise.reject(error));
    }
}

/**
 * Answers `null` where a request that only helps along failed
 *
 * Suggestions while typing are fetched in the background: when one of them
 * fails, the field still takes what is typed, so the page stays where it is
 * instead of landing on the error screen. An expired session still goes to the
 * login like everywhere else.
 *
 * @param promise the raw call, unreported
 *
 * @returns what came back, or `null` where the request failed
 */
async function quietly<T>(promise: Promise<T>): Promise<T | null> {
    try {
        return await promise;
    } catch (error) {
        if (error instanceof ResponseError && error.response.status === 401) {
            return handleError(Promise.reject(error));
        }
        console.error("Request failed quietly", error);
        return null;
    }
}

/**
 * Keep optional Account action failures with their caller while honoring session expiration.
 *
 * @param promise the generated request without global error reporting
 * @returns its response, or the original failure for local feedback
 */
async function localAccountAction<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (error) {
        if (error instanceof ResponseError && error.response.status === 401) {
            return handleError(Promise.reject(error));
        }
        throw error;
    }
}

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            if (e.response.status === 401) {
                SESSION_STORE.expired();
                throw redirect({ to: "/auth/login", search: { redirect: window.location.pathname } });
            } else {
                try {
                    const err = await e.response.json();
                    msg = `${e.response.statusText}. TraceId: ${err.trace_id}`;
                } catch {
                    console.error("Got invalid json", e.response.body);
                    msg = `${e.response.statusText}. The server's response was invalid json.`;
                }
            }
        } else if (e instanceof RequiredError) {
            console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}

/**
 * Keeps cosmetic background failures local while still respecting an expired session.
 *
 * @param request a background HTTP read
 * @returns its normal response, without reporting transport failures globally
 */
async function quietSessionRead<T>(request: Promise<T>): Promise<T> {
    try {
        return await request;
    } catch (error) {
        if (error instanceof ResponseError && error.response.status === 401) SESSION_STORE.expired();
        throw error;
    }
}
